import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import {
  HyvaksytynSopimuksenTekstiJaAvainRequest,
  HyvaksytynSopimuksenTekstiJaAvainResponse,
  LAST_SOPIMUS_VERSION_WITH_TEXT_IN_FIRESTORE,
  PalvelukuvauksenPdfRequest,
  PalvelukuvauksenPdfResponse,
  Palvelukuvaus,
  SopimuksenAllekirjoitanutRequest,
  SopimuksenAllekirjoitanutResponse,
  SopimuksenAllekirjoittajaJaAika,
  SOPIMUS_LIITE_NIMET,
  SOPIMUS_LIITETYYPIT,
  SopimusLiiteAsiakkaanHakuPyynto,
  SopimusLiiteAsiakkaanHakuVastaus,
  SopimusLiiteMap,
} from '@lemon/core-firebase/model/sopimus'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'

import { combineLatest, map, Observable, of as observableOf, Subject, switchMap, takeUntil, tap } from 'rxjs'

import { LemonaidAsiakkaanKuukausihinta, LemonaidAsiakkaanSopimuskausi } from '@lemon/core-firebase/model/kayttaja'
import { tuotenimikkeet } from '@lemon/core-firebase/model/tuotenimikkeet'
import { AsiakasJaettuLemonaidService } from '@lemon/core-firebase/service/asiakas-jaettu-lemonaid.service'
import { TuettuKieli } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { KayttajaService } from '../_angular/service/kayttaja.service'
import { TiedostojenLataamisService } from '../_jaettu-angular/service/tiedostojen-lataamis.service'

interface VeloituksetViewable {
  nimi: string
  hinta: number
  frekvenssi: string
  lisatiedot?: string
}

@Component({
  templateUrl: './sopimus-ja-ehdot.component.html',
  styleUrls: ['./sopimus-ja-ehdot.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SopimusJaEhdotComponent implements OnInit, OnDestroy {
  readonly commonError = signal<string>(undefined)
  readonly sopimusExists = signal<boolean>(undefined)
  readonly naytaVanhanSopimuksenLiitteet = signal(false)
  sopimusPdfObservable: Observable<SafeResourceUrl>
  sopimuksenAllekirjoitanutObservable: Observable<SopimuksenAllekirjoittajaJaAika[]>
  palvelukuvausTekstiObservable: Observable<string>
  valittuKieliObservable: Observable<TuettuKieli>

  private _latestSopimusBase64: string = null
  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private _sopimusPdfObjectUrl: string
  private _liitePdfObjectUrls: SopimusLiiteMap<string> = {}

  readonly liitetyypit = SOPIMUS_LIITETYYPIT
  readonly liiteNimet = SOPIMUS_LIITE_NIMET
  readonly liitePdfUrls = signal<SopimusLiiteMap<SafeResourceUrl>>({})

  nykyinenJaSeuraavaHintaObservable: Observable<{ nykyinen: LemonaidAsiakkaanKuukausihinta; seuraava: LemonaidAsiakkaanKuukausihinta }>
  sopimuskausiObservable: Observable<LemonaidAsiakkaanSopimuskausi>
  muutJatkuvatVeloituksetObservable: Observable<VeloituksetViewable[]>

  constructor(
    private _errorHandler: ErrorHandler,
    private _ladataanService: LadataanService,
    private _translationService: LemonTranslationService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _fileSaverService: FileSaverService,
    private _tiedostojenLataamisService: TiedostojenLataamisService,
    private _kayttajaService: KayttajaService,
    private _domSanitizer: DomSanitizer,
    private _asiakasJaettuService: AsiakasJaettuLemonaidService,
    private _dateService: DateService,
  ) {}

  ngOnInit() {
    this.sopimuskausiObservable = this._kayttajaService.nykyinenAsiakasObservable.pipe(
      map(asiakas => {
        if (!asiakas?.sopimuskaudet?.length) {
          return null
        }
        return this._asiakasJaettuService.annaNykyinenTaiVoimaanTulevaSopimuskausi(asiakas.sopimuskaudet)
      }),
    )

    this.nykyinenJaSeuraavaHintaObservable = this._kayttajaService.nykyisenAsiakkaanHinnatObservable.pipe(
      map(hinnat => {
        return this._asiakasJaettuService.annaNykyinenJaSeuraavaLemonaidHinta(hinnat)
      }),
    )

    this.muutJatkuvatVeloituksetObservable = combineLatest([this._kayttajaService.muutJatkuvatVeloituksetObservable, this._translationService.currentLanguageObservable]).pipe(
      map(([veloitukset, kieli]) => {
        return this._asiakasJaettuService.annaNykyisetMuutJatkuvatVeloitukset(veloitukset, this._dateService.currentLocalDate()).map(v => {
          const tuotenimike = tuotenimikkeet.find(t => t.avain === v.tuote)
          const tuoteNimi = kieli === 'en' ? tuotenimike.engNimi : tuotenimike.nimi
          const frekvenssiText = this._translationService.lokalisoiKielella('sopimus-ja-ehdot.' + v.frekvenssi, kieli)

          const veloitus: VeloituksetViewable = {
            nimi: tuoteNimi,
            hinta: v.hinta,
            frekvenssi: frekvenssiText,
            lisatiedot: v.lisatiedot,
          }
          return veloitus
        })
      }),
    )

    this.valittuKieliObservable = this._translationService.currentLanguageObservable.pipe(takeUntil(this._ngUnsubscribe))

    const sopimusDataObservable: Observable<HyvaksytynSopimuksenTekstiJaAvainResponse> = this._translationService.currentLanguageObservable.pipe(
      tap(() => this._ladataanService.aloitaLataaminen()),
      switchMap(kieli => {
        const req: HyvaksytynSopimuksenTekstiJaAvainRequest = {
          kieli: kieli || 'fi',
        }
        return this._firebaseLemonaid
          .functionsCall<HyvaksytynSopimuksenTekstiJaAvainRequest, HyvaksytynSopimuksenTekstiJaAvainResponse>('sopimusAnnaKayttajanViimeisinHyvaksyttyVersio', req)
          .then(resp => {
            if (!resp || (resp && resp.e)) {
              this._errorHandler.handleError(new Error(resp ? resp.e : 'unknown-error'))
              return null
            }
            this.naytaVanhanSopimuksenLiitteet.set(resp.sopimusAvain === LAST_SOPIMUS_VERSION_WITH_TEXT_IN_FIRESTORE)
            if (resp.sopimusAvain !== LAST_SOPIMUS_VERSION_WITH_TEXT_IN_FIRESTORE) {
              this._loadLiitteet(kieli)
            }
            return resp
          })
          .finally(() => this._ladataanService.lopetaLataaminen())
      }),
    )

    const palvelukuvausDataObservable: Observable<Palvelukuvaus> = this._firebaseLemonaid.firestoreDoc<Palvelukuvaus>('admin/palvelukuvaus').listen()
    this.palvelukuvausTekstiObservable = combineLatest([this._translationService.currentLanguageObservable, palvelukuvausDataObservable]).pipe(
      map(([kieli, palvelukuvaus]) => {
        if (!palvelukuvaus?.textInDiffLangs) {
          return ''
        }
        if (kieli === 'fi' || kieli === 'en') {
          return palvelukuvaus.textInDiffLangs[kieli]
        }
        return palvelukuvaus.textInDiffLangs['fi']
      }),
    )

    this.sopimusPdfObservable = sopimusDataObservable.pipe(
      map(sopimusData => {
        if (!sopimusData?.sopimusPdf) {
          this._latestSopimusBase64 = null
          return null
        }
        this._latestSopimusBase64 = sopimusData.sopimusPdf
        if (this._sopimusPdfObjectUrl) {
          URL.revokeObjectURL(this._sopimusPdfObjectUrl)
        }
        this.sopimusExists.set(true)
        const uint8Array = this._tiedostojenLataamisService.base64ToUint8Array(sopimusData.sopimusPdf)
        const blob = new Blob([uint8Array], { type: 'application/pdf' })
        this._sopimusPdfObjectUrl = URL.createObjectURL(blob)
        return this._domSanitizer.bypassSecurityTrustResourceUrl(this._sopimusPdfObjectUrl)
      }),
    )

    this.sopimuksenAllekirjoitanutObservable = sopimusDataObservable.pipe(
      switchMap(sopimusData => {
        if (!sopimusData?.sopimusAvain) {
          return observableOf<SopimuksenAllekirjoittajaJaAika[]>([])
        }

        const req: SopimuksenAllekirjoitanutRequest = {
          sopimusAvain: sopimusData.sopimusAvain,
        }
        return this._firebaseLemonaid.functionsCall<SopimuksenAllekirjoitanutRequest, SopimuksenAllekirjoitanutResponse>('sopimusAnnaAllekirjoittajienTiedot', req).then(resp => {
          if (!resp || (resp && resp.e)) {
            this._errorHandler.handleError(new Error(resp ? resp.e : 'unknown-error'))
            return null
          }
          if (!resp?.allekirjoitanut?.length) {
            return []
          }
          return resp.allekirjoitanut
        })
      }),
    )
  }

  private async _loadLiitteet(kieli: TuettuKieli) {
    for (const tyyppi of SOPIMUS_LIITETYYPIT) {
      if (this._liitePdfObjectUrls[tyyppi]) {
        URL.revokeObjectURL(this._liitePdfObjectUrls[tyyppi])
        this._liitePdfObjectUrls[tyyppi] = null
      }
    }
    this.liitePdfUrls.set({})
    const results = await Promise.allSettled(
      SOPIMUS_LIITETYYPIT.map(async tyyppi => {
        const req: SopimusLiiteAsiakkaanHakuPyynto = { liitetyyppi: tyyppi, kieli }
        const vastaus = await this._firebaseLemonaid.functionsCall<SopimusLiiteAsiakkaanHakuPyynto, SopimusLiiteAsiakkaanHakuVastaus>('sopimusAnnaLiite', req)
        return { tyyppi, base64: vastaus?.base64String }
      }),
    )
    const newUrls: SopimusLiiteMap<SafeResourceUrl> = {}
    for (const result of results) {
      if (result.status === 'fulfilled' && result.value.base64) {
        const blob = new Blob([this._tiedostojenLataamisService.base64ToUint8Array(result.value.base64)], { type: 'application/pdf' })
        const objectUrl = URL.createObjectURL(blob)
        this._liitePdfObjectUrls[result.value.tyyppi] = objectUrl
        newUrls[result.value.tyyppi] = this._domSanitizer.bypassSecurityTrustResourceUrl(objectUrl)
      }
    }
    this.liitePdfUrls.set(newUrls)
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
    if (this._sopimusPdfObjectUrl) {
      URL.revokeObjectURL(this._sopimusPdfObjectUrl)
    }
    for (const tyyppi of SOPIMUS_LIITETYYPIT) {
      if (this._liitePdfObjectUrls[tyyppi]) {
        URL.revokeObjectURL(this._liitePdfObjectUrls[tyyppi])
      }
    }
  }

  downloadSopimus() {
    if (!this._latestSopimusBase64) {
      this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
      this._errorHandler.handleError('no-sopimus-base64')
      return
    }
    this._downloadSopimusPdf(this._latestSopimusBase64)
  }

  private _downloadSopimusPdf(base64File: string) {
    const uint8Array = this._tiedostojenLataamisService.base64ToUint8Array(base64File)
    const fileName = 'Tilitoimisto Lemon Tree Oy Toimeksiantosopimus.pdf'
    this._fileSaverService.saveAs(new Blob([uint8Array], { type: 'application/pdf' }), fileName)
  }

  async downloadPalvelukuvaus() {
    this._ladataanService.aloitaLataaminen()

    const req: PalvelukuvauksenPdfRequest = {
      kieli: this._translationService.nykyinenKieli || 'fi',
    }
    return this._firebaseLemonaid
      .functionsCall<PalvelukuvauksenPdfRequest, PalvelukuvauksenPdfResponse>('sopimusAnnaPalvelukuvausPdf', req)
      .then(response => {
        if (!response || response.e || !response.base64String) {
          this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe', this._translationService.nykyinenKieli))
          this._errorHandler.handleError(response ? response.e : 'no-response')
        } else {
          this._downloadPalvelukuvausPdf(response.base64String)
        }
      })
      .catch(err => this._errorHandler.handleError(err))
      .finally(() => {
        this._ladataanService.lopetaLataaminen()
      })
  }

  private _downloadPalvelukuvausPdf(base64File: string) {
    const uint8Array = this._tiedostojenLataamisService.base64ToUint8Array(base64File)
    const fileName = 'Tilitoimisto Lemon Tree Oy Palvelukuvaus.pdf'
    this._fileSaverService.saveAs(new Blob([uint8Array], { type: 'application/pdf' }), fileName)
  }
}
